import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { Plus, RefreshCcw, Bell } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Link } from "react-router-dom";

function NewPolicyDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: customers = [] } = useQuery({ queryKey: ["cust-lookup"], queryFn: async () => (await api.get("/customers", { params: { limit: 500 } })).data });
  const [f, setF] = useState({
    customer_id: "", policy_type: "car", provider: "", policy_number: "",
    vehicle_number: "", vehicle_type: "car", sum_insured: "", idv: "", ncb: "",
    premium: "", start_date: "", end_date: "", notes: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const mut = useMutation({
    mutationFn: async () => (await api.post("/insurance", {
      ...f,
      sum_insured: f.sum_insured ? Number(f.sum_insured) : null,
      idv: f.idv ? Number(f.idv) : null,
      ncb: f.ncb ? Number(f.ncb) : null,
      premium: f.premium ? Number(f.premium) : null,
    })).data,
    onSuccess: () => { toast.success("Policy added"); qc.invalidateQueries({ queryKey: ["ins-all"] }); qc.invalidateQueries({ queryKey: ["ins-renewals"] }); setOpen(false); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="bg-[#0052FF] hover:bg-[#003FCC] text-white text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="new-policy-btn"><Plus size={14} /> New Policy</button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader><DialogTitle>New Insurance Policy</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); if (!f.customer_id) { toast.error("Pick a customer"); return; } mut.mutate(); }} className="space-y-3" data-testid="new-policy-form">
          <div>
            <Label>Customer *</Label>
            <Select value={f.customer_id} onValueChange={(v) => set("customer_id")(v)}>
              <SelectTrigger data-testid="policy-customer"><SelectValue placeholder="Choose customer" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name} · {c.phone}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Policy type</Label>
              <Select value={f.policy_type} onValueChange={(v) => set("policy_type")(v)}>
                <SelectTrigger data-testid="policy-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="car">Car Insurance</SelectItem>
                  <SelectItem value="cv">Commercial Vehicle</SelectItem>
                  <SelectItem value="bike">Bike Insurance</SelectItem>
                  <SelectItem value="health">Health (future)</SelectItem>
                  <SelectItem value="life">Life (future)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Provider</Label><Input value={f.provider} onChange={set("provider")} required data-testid="policy-provider" /></div>
            <div><Label>Policy number</Label><Input value={f.policy_number} onChange={set("policy_number")} data-testid="policy-number" /></div>
            <div><Label>Vehicle number</Label><Input value={f.vehicle_number} onChange={set("vehicle_number")} data-testid="policy-vehicle" /></div>
            <div><Label>IDV (₹)</Label><Input type="number" value={f.idv} onChange={set("idv")} /></div>
            <div><Label>NCB (%)</Label><Input type="number" value={f.ncb} onChange={set("ncb")} /></div>
            <div><Label>Premium (₹)</Label><Input type="number" value={f.premium} onChange={set("premium")} data-testid="policy-premium" /></div>
            <div><Label>Sum insured (₹)</Label><Input type="number" value={f.sum_insured} onChange={set("sum_insured")} /></div>
            <div><Label>Start date</Label><Input type="date" value={f.start_date} onChange={set("start_date")} /></div>
            <div><Label>Expiry date</Label><Input type="date" value={f.end_date} onChange={set("end_date")} data-testid="policy-end-date" /></div>
          </div>
          <button type="submit" disabled={mut.isPending} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-2.5 rounded-full disabled:opacity-60" data-testid="policy-submit">
            {mut.isPending ? "Saving…" : "Save Policy"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Insurance() {
  const { data: policies = [] } = useQuery({ queryKey: ["ins-all"], queryFn: async () => (await api.get("/insurance")).data });
  const { data: renewals = [] } = useQuery({ queryKey: ["ins-renewals"], queryFn: async () => (await api.get("/insurance/renewals", { params: { days: 30 } })).data });

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#06152D]">Insurance</h1>
          <p className="text-sm text-[#52525B]">Vehicle, CV, and future health/life policies. Track renewals proactively.</p>
        </div>
        <NewPolicyDialog />
      </div>

      <Tabs defaultValue="renewals" className="mt-6">
        <TabsList className="bg-white border border-[#E4E4E7]">
          <TabsTrigger value="renewals" data-testid="tab-renewals"><Bell size={12} className="mr-1" /> Renewals due ({renewals.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all-policies">All policies ({policies.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="renewals" className="mt-4">
          <div className="bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
            <table className="w-full lj-table" data-testid="renewals-table">
              <thead><tr><th>Expiry</th><th>Customer</th><th>Type</th><th>Vehicle No.</th><th>Provider</th><th>Policy No.</th><th>Premium</th></tr></thead>
              <tbody>
                {renewals.length === 0 && <tr><td colSpan="7" className="text-center text-[#52525B] py-8">No policies expiring in the next 30 days.</td></tr>}
                {renewals.map((p) => (
                  <tr key={p.id} data-testid={`renewal-row-${p.id}`}>
                    <td><Badge variant="secondary" className="bg-amber-100 text-amber-800">{p.end_date}</Badge></td>
                    <td><Link to={`/admin/customers/${p.customer_id}`} className="text-[#0052FF] hover:underline">Open</Link></td>
                    <td>{p.policy_type}</td>
                    <td className="font-mono-num text-xs">{p.vehicle_number || "—"}</td>
                    <td>{p.provider}</td>
                    <td className="font-mono-num text-xs">{p.policy_number || "—"}</td>
                    <td className="font-mono-num">{p.premium ? inr(p.premium) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="all" className="mt-4">
          <div className="bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
            <table className="w-full lj-table" data-testid="all-policies-table">
              <thead><tr><th>Type</th><th>Customer</th><th>Provider</th><th>Policy No.</th><th>Vehicle No.</th><th>Premium</th><th>Start</th><th>Expiry</th></tr></thead>
              <tbody>
                {policies.length === 0 && <tr><td colSpan="8" className="text-center text-[#52525B] py-8">No policies yet.</td></tr>}
                {policies.map((p) => (
                  <tr key={p.id} data-testid={`policy-row-${p.id}`}>
                    <td>{p.policy_type}</td>
                    <td><Link to={`/admin/customers/${p.customer_id}`} className="text-[#0052FF] hover:underline">Open</Link></td>
                    <td>{p.provider}</td>
                    <td className="font-mono-num text-xs">{p.policy_number || "—"}</td>
                    <td className="font-mono-num text-xs">{p.vehicle_number || "—"}</td>
                    <td className="font-mono-num">{p.premium ? inr(p.premium) : "—"}</td>
                    <td className="text-xs">{p.start_date || "—"}</td>
                    <td className="text-xs">{p.end_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
